import { TaskState, type UserInfo } from "@iflyrpa/share";
import type { Response } from "playwright-core";
import { ExecutionState } from "../../types";
import { ProxyAgent } from "../../utils/proxy";
import type { SteelAction } from "./index";

const scanRetryMaxCount = 60; // 二维码扫描重试次数
const waitQrcodeResultMaxTime = scanRetryMaxCount * 2 * 1000; // 最长等待时间

export const rpaServer: SteelAction = async (task, params) => {
    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");
    let executionState: ExecutionState | undefined;

    let proxyUrl: string | undefined;
    if (params.proxyLoc) {
        task.logger?.info(`==> 开始获取代理信息：${params}`);
        const ProxyAgentResult = await ProxyAgent({ logger: task.logger }, params.proxyLoc, params.accountId);
        task.logger?.info("==> 代理信息获取成功！");
        proxyUrl = ProxyAgentResult ? `http://${ProxyAgentResult.ip}:${ProxyAgentResult.port}` : undefined;
    }
    const page = await task.createPage({
        url: "https://mp.toutiao.com/auth/page/login",
        proxyUrl,
        userAgent: params.userAgent,
        ...(params.viewport ? { viewport: params.viewport } : {}),
    });

    task.steelBrowser?.on("disconnected", async () => {
        const browserContext = task.steelBrowserContext;
        const browser = task.steelBrowser;
        executionState = ExecutionState.BROWSER_CLOSED;
        await page.close();

        if (browserContext) {
            await browserContext.close();
        }
        if (browser) {
            await browser.close();
        }
    });

    // 拦截不必要的资源请求
    // await page.route(/\/welcome.*\.(png|mp4)$/i, (route) => route.abort());

    await updateTaskState?.({
        state: TaskState.WAIT_SCAN,
        connectAddress: await task.steelConnector?.getLive(task.sessionId || ""),
        sessionId: task.sessionId,
    });

    let userInfo: UserInfo = { uniqueId: "", avatar: "", name: "" };

    try {
        await new Promise<void>((resolve, reject) => {
            let finished = false;

            const timer = setTimeout(async () => {
                cleanup();
                executionState = ExecutionState.TIMEOUT;
                await updateTaskState?.({
                    state: TaskState.TIMEOUT,
                    error: "等待扫码登录超时",
                });
                resolve();
            }, waitQrcodeResultMaxTime);

            const cleanup = () => {
                if (finished) return; // 防止多次调用
                finished = true;
                clearTimeout(timer);
                page.off("response", handleWebCommon);
                page.off("response", handleCheckQR);
                task.steelBrowser?.off("disconnected", handleBrowserClosed);
            };

            const handleBrowserClosed = () => {
                cleanup();
                executionState = ExecutionState.BROWSER_CLOSED;
                resolve();
            };

            const handleWebCommon = async (resp: Response) => {
                try {
                    const url = resp.url();

                    // 登录成功后的第一个请求
                    if (url.includes("/web/common")) {
                        cleanup();

                        const headers = resp.headers();
                        const xmst = headers["x-ms-token"];
                        const date = headers["x-tt-timestamp"];

                        if (xmst && date) {
                            const mstExpirDate = new Date(Number(date) * 1000 + 7 * 24 * 60 * 60 * 1000);
                            const msToken = {
                                name: "msToken",
                                value: xmst,
                                domain: ".bytedance.com",
                                expires: mstExpirDate.getTime() / 1000,
                                httpOnly: true,
                                path: "/",
                                sameSite: "None" as const,
                                secure: true,
                            };
                            await page.context().addCookies([msToken]);
                            await updateTaskState?.({
                                state: TaskState.CONFIRMED,
                            });
                        }

                        executionState = ExecutionState.SUCCESS;
                        resolve();
                    } else if (url.includes("/mp/agw/creator_center/user_info")) {
                        // 优先获取用户信息
                        try {
                            const jsonUserInfo = await resp.json();
                            userInfo = {
                                name: jsonUserInfo.name ?? "",
                                avatar: jsonUserInfo.avatar_url ?? "",
                                uniqueId: jsonUserInfo.user_id_str ?? "",
                            };
                        } catch (e) {
                            task.logger.warn("无法解析用户信息:", { url, error: e });
                        }
                    }
                } catch (err) {
                    cleanup();
                    reject(err);
                }
            };

            // 0=用户未扫码，5=用户扫码已超时，2=用户已扫码未确认，3=扫码成功
            const handleCheckQR = async (response: Response) => {
                try {
                    const url = response.url();
                    if (!url.includes("/check_qrconnect/")) return;

                    let jsonResponse: { error_code: number; data?: { status: string } };
                    try {
                        const text = await response.text();
                        if (!text) {
                            task.logger.warn("Empty response body:", { url });
                            return;
                        }

                        jsonResponse = JSON.parse(text);
                    } catch (e) {
                        task.logger.warn("Failed to parse json:", { url, error: e });
                        return;
                    }

                    if (jsonResponse.error_code === 0 && jsonResponse.data?.status === "2") {
                        // 扫码成功
                        await updateTaskState?.({
                            state: TaskState.SCANNED,
                        });
                    }
                    if (jsonResponse.error_code === 0 && jsonResponse.data?.status === "3") {
                        page.off("response", handleCheckQR);
                        page.on("response", handleWebCommon);
                    }
                } catch (err) {
                    cleanup();
                    reject(err);
                }
            };

            // 在页面加载之前就注册监听，防止丢失请求
            task.steelBrowser?.on("disconnected", handleBrowserClosed);
            page.on("response", handleCheckQR);
        });
    } finally {
        await page.close();
    }

    if (executionState === undefined) return { code: 500, message: "未知错误", data: {} };

    switch (executionState) {
        case ExecutionState.BROWSER_CLOSED: {
            const msg = "浏览器已关闭，任务中断!";
            await updateTaskState?.({
                state: TaskState.FAILED,
                error: "浏览器已关闭",
            });
            return { code: 414, message: msg, data: {} };
        }

        case ExecutionState.TIMEOUT: {
            const msg = "等待用户扫码登录超时!";
            await updateTaskState?.({
                state: TaskState.TIMEOUT,
                error: msg,
            });
            task.logger.info(msg);
            return { code: 414, message: msg, data: {} };
        }

        case ExecutionState.SUCCESS:
            await updateTaskState?.({
                state: TaskState.SUCCESS,
                result: {
                    cookie: JSON.stringify(await task.steelBrowserContext?.cookies()),
                    userInfo: userInfo,
                },
            });
            return {
                code: 0,
                message: "成功",
                data: {
                    cookie: JSON.stringify(await task.steelBrowserContext?.cookies()),
                    userInfo,
                },
            };
        default:
            // 处理 RUNNING 和其他可能的状态
            return { code: 500, message: "任务执行中断", data: {} };
    }
};
